/**
 * useFlowRecorder
 * ───────────────
 * Records every highlight + chat message the agent sends while in
 * "record mode". When the agent stops recording, the accumulated steps
 * are returned as a SequenceSession that can be saved to the library.
 *
 * HOW IT WORKS
 * ────────────
 * The agent turns on recording, then works normally:
 *   • Highlights elements   → each element becomes a SequenceStep
 *   • Types a chat message  → that message becomes the TextEn for the
 *                             step that was highlighted just before it
 *
 * The pairing rule is simple:
 *   "The text typed after a highlight belongs to that highlight step."
 *
 * If the agent types a message without a preceding highlight, it is
 * recorded as a text-only step (elementId = null) — useful for
 * explaining context without pointing at a specific button.
 *
 * USAGE in App.tsx
 * ────────────────
 *   const recorder = useFlowRecorder();
 *
 *   // Wrap existing handlers:
 *   const handleSendHighlight = (userId, seq, ms) => {
 *     sendHighlight(userId, seq, ms);
 *     recorder.recordHighlight(seq, ms, uiElements);
 *   };
 *
 *   const handleSendChat = async (user, text, translated, usedLLM) => {
 *     await sendAgentChat(user.id, text);
 *     pushChat(...);
 *     recorder.recordText(text);          // ← add this line
 *   };
 *
 *   // Pass recorder to GuidancePanel:
 *   <GuidancePanel ... recorder={recorder} />
 */

import { useState, useRef, useCallback } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface RecordedStep {
  /** LP_ element ID — null for text-only steps */
  elementId:      string | null;
  /** Human-readable element label */
  label:          string;
  /** Instruction text the agent typed in English */
  textEn:         string;
  /** Route where this element lives */
  route?:         string;
  /** Highlight style used at record time */
  highlightStyle: 'redPulse' | 'gold';
  /** Whether to wait for a click before advancing */
  waitForClick:   boolean;
  /** Delay between steps in ms */
  intervalMs:     number;
  /** Timestamp, used to compute duration */
  recordedAt:     number;
}

export interface RecorderState {
  isRecording: boolean;
  steps:       RecordedStep[];
  startedAt:   number | null;
}

export interface FlowRecorder {
  state:           RecorderState;
  start:           () => void;
  stop:            () => RecordedStep[];
  discard:         () => void;
  recordHighlight: (
    elementIds: string[],
    intervalMs: number,
    elements:   { id: string; label: string; route?: string }[],
    style?:     'redPulse' | 'gold',
  ) => void;
  recordText:      (textEn: string) => void;
  undoLastStep:    () => void;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useFlowRecorder(): FlowRecorder {
  const [state, setState] = useState<RecorderState>({
    isRecording: false,
    steps:       [],
    startedAt:   null,
  });

  // Use a ref so recordHighlight / recordText always see the latest steps
  // without needing to be recreated on every render.
  const stepsRef = useRef<RecordedStep[]>([]);

  const start = useCallback(() => {
    stepsRef.current = [];
    setState({ isRecording: true, steps: [], startedAt: Date.now() });
  }, []);

  const stop = useCallback((): RecordedStep[] => {
    const captured = [...stepsRef.current];
    setState(s => ({ ...s, isRecording: false }));
    return captured;
  }, []);

  const discard = useCallback(() => {
    stepsRef.current = [];
    setState({ isRecording: false, steps: [], startedAt: null });
  }, []);

  /**
   * Called every time the agent sends a highlight to a user.
   * Each element in the sequence becomes a separate step.
   *
   * The highlight text is left blank ("") — the agent fills it in by
   * typing a chat message immediately after the highlight (recordText).
   * If no message follows, the step uses the element label as fallback text.
   */
  const recordHighlight = useCallback((
    elementIds: string[],
    intervalMs: number,
    elements:   { id: string; label: string; route?: string }[],
    style:      'redPulse' | 'gold' = 'redPulse',
  ) => {
    setState(s => {
      if (!s.isRecording) return s;

      const now = Date.now();
      const newSteps: RecordedStep[] = elementIds.map(id => {
        const el = elements.find(e => e.id === id);
        return {
          elementId:      id,
          label:          el?.label ?? id,
          textEn:         '',          // filled by the next recordText() call
          route:          el?.route,
          highlightStyle: style,
          waitForClick:   true,
          intervalMs,
          recordedAt:     now,
        };
      });

      const updated = [...s.steps, ...newSteps];
      stepsRef.current = updated;
      return { ...s, steps: updated };
    });
  }, []);

  /**
   * Called every time the agent sends a chat message.
   * Attaches the message to the most recent step that has no text yet.
   * If all steps already have text, creates a new text-only step.
   */
  const recordText = useCallback((textEn: string) => {
    if (!textEn.trim()) return;

    setState(s => {
      if (!s.isRecording) return s;

      const steps = [...s.steps];

      // Find the last step with no text — fill it in
      const emptyIdx = steps.map((st, i) => [st, i] as const)
                            .reverse()
                            .find(([st]) => st.textEn === '');

      if (emptyIdx) {
        const [, idx] = emptyIdx;
        steps[idx] = { ...steps[idx], textEn };
      } else {
        // No empty step — text-only step (context annotation)
        steps.push({
          elementId:      null,
          label:          'Message',
          textEn,
          highlightStyle: 'gold',
          waitForClick:   false,
          intervalMs:     2000,
          recordedAt:     Date.now(),
        });
      }

      stepsRef.current = steps;
      return { ...s, steps };
    });
  }, []);

  const undoLastStep = useCallback(() => {
    setState(s => {
      if (!s.isRecording || s.steps.length === 0) return s;
      const steps = s.steps.slice(0, -1);
      stepsRef.current = steps;
      return { ...s, steps };
    });
  }, []);

  return { state, start, stop, discard, recordHighlight, recordText, undoLastStep };
}
